import { useState, useMemo, useEffect, useRef } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../ui/popover";
import { Search, Users, Shield, CheckCircle2, X } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../ui/tooltip";
import { PublishComponentProps } from "./interfaces/CommonInterfaces";

export default function PublishComponent({
  entityName,
  entityType = "device",
  availableRoles,
  publishedRoles,
  onPublish,
  disabled = false,
  buttonVariant = "outline",
  buttonSize = "sm",
  cardStyle = false
}: PublishComponentProps & { cardStyle?: boolean }) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const [yOffset, setYOffset] = useState(0);
  const popoverRef = useRef<HTMLDivElement | null>(null);

  const filteredRoles = useMemo(() => {
    const rolesForDisplay = entityType === "url"
      ? availableRoles.filter((role) => role.name.toLowerCase() !== "epam-user")
      : availableRoles;

    if (!searchQuery.trim()) return rolesForDisplay;
    return rolesForDisplay.filter(role =>
      role.name.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [availableRoles, searchQuery, entityType]);

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (open) {
      setSelectedRoles(publishedRoles.map(String));
      setSearchQuery("");
      setTimeout(() => adjustPopoverPosition(), 0);
    }
  };

  const handleRoleToggle = (roleId: string, checked: boolean) => {
    setSelectedRoles(prev =>
      checked ? [...prev, roleId] : prev.filter(id => id !== roleId)
    );
  };

  const handleSubmit = () => {
    onPublish(selectedRoles);
    setIsOpen(false);
  };

  const publishedRoleNames = publishedRoles
    ?.map((roleId) => {
      const role = availableRoles.find(
        (roleData) => roleData?.id?.toString() === roleId?.toString()
      );
      return role ? role.name : roleId;
    })
    .filter((roleName) => entityType !== "url" || roleName?.toLowerCase() !== "epam-user");

  const adjustPopoverPosition = () => {
    const el = popoverRef.current;
    if (!el) return;

    const rect = el.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const padding = 12;
    let correction = 0;

    if (rect.bottom > viewportHeight - padding) {
      correction = viewportHeight - rect.bottom - padding;
    } else if (rect.top < padding) {
      correction = padding - (rect.top - 20);
    }

    setYOffset(correction);
  };

  useEffect(() => {
    if (!isOpen) return;
    const listener = () => adjustPopoverPosition();
    window.addEventListener("scroll", listener, true);
    window.addEventListener("resize", listener);
    return () => {
      window.removeEventListener("scroll", listener, true);
      window.removeEventListener("resize", listener);
    };
  }, [isOpen]);

  return (
    <div className="flex flex-col items-start gap-1">
      <Popover open={isOpen} onOpenChange={handleOpenChange}>
        <PopoverTrigger asChild>
          <Button
            variant={buttonVariant}
            size={buttonSize}
            disabled={disabled}
            data-testid="button-publish"
            className="text-primary hover:underline"
          >
            Publish
          </Button>
        </PopoverTrigger>

        <PopoverContent
          ref={popoverRef}
          align="end"
          side="top"
          onOpenAutoFocus={(e) => e.preventDefault()}
          className={`w-[380px] p-0 border-slate-200 dark:border-slate-700 shadow-2xl rounded-xl ${cardStyle ? "web-url-publish" : ""}`}
          style={{
            transform: `translateY(${yOffset}px)`,
            transition: "transform 0.2s ease",
          }}
        >
          <button
            className="absolute top-4 right-4 text-slate-500 dark:text-slate-300 hover:text-slate-700 dark:hover:text-slate-100"
            onClick={() => setIsOpen(false)}
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>

          <div className="space-y-0">
            <div className="p-6 pb-4 border-b border-slate-200/80 dark:border-slate-700/80 bg-gradient-to-r from-blue-50/50 via-white to-slate-50/50 dark:from-blue-950/20 dark:via-slate-800 dark:to-slate-900/50">
              <div className="flex items-center gap-3">
                <div className="p-2 text-white shadow-lg rounded-xl bg-gradient-to-br from-blue-500 to-blue-600">
                  <Users className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-slate-800 dark:text-slate-100">
                    Publish Roles
                  </h3>
                  <p className="mt-1 text-xs text-slate-600 dark:text-slate-300">
                    Grant{" "}
                    <span className="font-semibold text-blue-600 dark:text-blue-400">
                      {entityName}
                    </span>{" "}
                    access to resources for selected roles
                  </p>
                </div>
              </div>
            </div>

            <div className="p-6 pb-4">
              <div className="relative">
                <Search className="absolute w-4 h-4 transform -translate-y-1/2 left-4 top-1/2 text-slate-400 dark:text-slate-500" />
                <Input
                  placeholder="Search roles..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="h-10 pl-12 text-sm transition-colors shadow-sm border-slate-200 dark:border-slate-600 bg-slate-50/50 dark:bg-slate-800/50 focus:bg-white dark:focus:bg-slate-700 rounded-xl"
                  data-testid="input-role-search"
                />
              </div>
            </div>

            <div className="px-5 pb-3">
              <div className="space-y-1.5 max-h-44 overflow-y-auto custom-scrollbar">
                {filteredRoles.length > 0 ? (
                  filteredRoles.map((role) => (
                    <div
                      key={role.id}
                      className={`p-2.5 rounded-lg border transition-all duration-200 cursor-pointer hover:elevate group ${selectedRoles.includes(role.id)
                        ? "border-blue-200 dark:border-blue-700 bg-blue-50/50 dark:bg-blue-950/20"
                        : "border-slate-200 dark:border-slate-700 bg-white/50 dark:bg-slate-800/50 hover:border-blue-300 dark:hover:border-blue-600"
                        }`}
                      onClick={() =>
                        handleRoleToggle(role.id, !selectedRoles.includes(role.id))
                      }
                      data-testid={`role-${role.id}`}
                    >
                      <div className="flex items-center gap-2.5">
                        <div className="p-1 transition-colors rounded bg-slate-100 dark:bg-slate-700 group-hover:bg-blue-100 dark:group-hover:bg-blue-900/30">
                          <Shield className="w-3 h-3 text-slate-600 dark:text-slate-400 group-hover:text-blue-600 dark:group-hover:text-blue-400" />
                        </div>
                        <div className="flex-1">
                          <span className="text-xs font-medium transition-colors cursor-pointer text-slate-700 dark:text-slate-200 group-hover:text-blue-700 dark:group-hover:text-blue-300">
                            {role.name}
                          </span>
                        </div>
                        {selectedRoles.includes(role.id) && (
                          <CheckCircle2 className="w-3.5 h-3.5 text-blue-600 dark:text-blue-400" />
                        )}
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="py-4 text-center text-slate-500 dark:text-slate-400">
                    <Shield className="w-5 h-5 mx-auto mb-2 text-slate-300 dark:text-slate-600" />
                    <p className="text-xs font-medium">No roles found</p>
                    <p className="mt-1 text-xs text-slate-400 dark:text-slate-500">
                      Try adjusting your search criteria
                    </p>
                  </div>
                )}
              </div>
            </div>

            <div className="p-5 pt-3 border-t border-slate-200/80 dark:border-slate-700/80 bg-gradient-to-r from-slate-50/30 via-white to-slate-50/30 dark:from-slate-900/30 dark:via-slate-800 dark:to-slate-900/30">
              <Button
                onClick={handleSubmit}
                className="w-full h-10 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-medium text-sm rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                data-testid="button-submit-publish"
              >
                <div className="flex items-center gap-2">
                  <Users className="w-3.5 h-3.5" />
                  <span>
                    {selectedRoles.length === 0
                      ? "UnPublish Roles"
                      : `Publish to ${selectedRoles.length} Role${selectedRoles.length === 1 ? "" : "s"
                      }`}
                  </span>
                </div>
              </Button>
            </div>
          </div>
        </PopoverContent>
      </Popover>

      {entityType !== "SSH user" && (
        <div className="text-xs text-muted-foreground">
          {publishedRoleNames.length > 0 ? (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    className="inline-flex items-center gap-1 text-left text-primary transition-colors hover:text-primary/80"
                    data-testid="button-show-more-roles"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <span className="font-medium whitespace-nowrap text-foreground">
                      Current Roles: {publishedRoleNames.length}
                    </span>
                  </button>
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-sm p-3">
                  <div className="text-sm">
                    <div className="mb-2 font-semibold text-foreground">
                      All Published Roles ({publishedRoleNames.length})
                    </div>
                    <div className="space-y-1">
                      {publishedRoleNames.map((roleName, index) => (
                        <div key={index} className="text-muted-foreground">
                          • {roleName}
                        </div>
                      ))}
                    </div>
                  </div>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ) : (
            <span
              className="italic text-muted-foreground"
              data-testid="text-no-published-roles"
            >
              None
            </span>
          )}
        </div>
      )}
    </div>
  );
}
